/**
 * MiraOps glyphs: the app's own icon set (no third-party icons).
 *
 * One family: 24px grid, 2px safe area, round caps and joins, 1.75 default stroke,
 * corner radius 2–2.5 on boxes, small solid dots as accents. Every glyph is distinct;
 * sidebar sections each have their own. `fill="currentColor"` (or `filled`) switches a
 * glyph to its solid form (used for the active sidebar item), filling only the parts
 * that read well solid.
 *
 * Props mirror the usual icon API (size, strokeWidth, className, aria-*), so screens
 * use them like any icon component. Decorative by default (aria-hidden) unless an
 * aria-label is given.
 */
import type { ReactNode, SVGProps } from 'react';

export type GlyphProps = Omit<SVGProps<SVGSVGElement>, 'ref' | 'fill'> & {
  size?: number | string; strokeWidth?: number | string; fill?: string; filled?: boolean;
};

type Draw = (solid: boolean) => ReactNode;
const dot = (cx: number, cy: number, r = 1.05) => <circle cx={cx} cy={cy} r={r} fill="currentColor" stroke="none" />;
const solidFill = (solid: boolean) => (solid ? 'currentColor' : 'none');

function glyph(name: string, draw: Draw) {
  const Glyph = ({ size = 24, strokeWidth = 1.75, className = '', fill, filled, ...rest }: GlyphProps) => {
    const solid = filled ?? (fill !== undefined && fill !== 'none');
    const labelled = rest['aria-label'] !== undefined;
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" focusable="false" role={labelled ? 'img' : undefined}
        aria-hidden={labelled ? undefined : true} className={`glyph glyph-${name}${className ? ` ${className}` : ''}`} {...rest}>
        {draw(solid)}
      </svg>
    );
  };
  Glyph.displayName = `Glyph(${name})`;
  return Glyph;
}

// ---------- Sidebar sections (each unique) ----------
export const House = glyph('today', s => <>
  <path d="M3.5 10.6 12 3.9l8.5 6.7" />
  <path d="M5.75 9v9.25a1.75 1.75 0 0 0 1.75 1.75h9a1.75 1.75 0 0 0 1.75-1.75V9" fill={solidFill(s)} />
  <path d="M10 20v-4.25a2 2 0 0 1 4 0V20" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
</>);
export const Building2 = glyph('companies', s => <>
  <rect x="4" y="3.5" width="10.5" height="17" rx="1.75" fill={solidFill(s)} />
  <path d="M14.5 9h3.75A1.75 1.75 0 0 1 20 10.75v9.75" />
  <path d="M2.5 20.5h19" />
  {s ? null : <>{dot(7.6, 7.6, .9)}{dot(10.9, 7.6, .9)}{dot(7.6, 11, .9)}{dot(10.9, 11, .9)}{dot(7.6, 14.4, .9)}{dot(10.9, 14.4, .9)}{dot(17.25, 13, .9)}{dot(17.25, 16.4, .9)}</>}
</>);
/** Dispatch: a car hauler — the vehicle being moved sits on the deck. */
export const Truck = glyph('dispatch', s => <>
  <path d="M2.75 16.25V8.5a1.5 1.5 0 0 1 1.5-1.5h9.5v9.25" fill={solidFill(s)} />
  <path d="M13.75 9.75h3.6a1.5 1.5 0 0 1 1.2.6l2.15 2.85a1.5 1.5 0 0 1 .3.9v2.15H19.6" fill={solidFill(s)} />
  <path d="M15.4 16.25H8.6M4.4 16.25H2.75" />
  <circle cx="6.5" cy="17" r="2" fill={s ? 'currentColor' : 'var(--c-surface, #fff)'} />
  <circle cx="17.5" cy="17" r="2" fill={s ? 'currentColor' : 'var(--c-surface, #fff)'} />
  {s ? null : <path d="M5.5 11.75h5.5l-1.1-1.9a.9.9 0 0 0-.8-.45H7.4a.9.9 0 0 0-.8.45z" />}
</>);
export const MapPinned = glyph('routes', s => <>
  <path d="M16.5 12.5s3.75-3.1 3.75-6.25a3.75 3.75 0 0 0-7.5 0c0 3.15 3.75 6.25 3.75 6.25z" fill={solidFill(s)} />
  {s ? null : dot(16.5, 6.25, 1.15)}
  <circle cx="5.5" cy="18.5" r="2" />
  <path d="M7.5 18.5h6.25a2.5 2.5 0 0 0 0-5H9.5a2.25 2.25 0 0 1 0-4.5h1.25" strokeDasharray={s ? undefined : '0 0'} />
</>);
/** Documents: a stack of pages (a single page is FileText). */
export const DocumentStack = glyph('documents', s => <>
  <path d="M8 6.5V5.25A1.75 1.75 0 0 1 9.75 3.5h6.5l3.25 3.25v9.5A1.75 1.75 0 0 1 17.75 18H16.5" />
  <path d="M12.5 6.5H6.25A1.75 1.75 0 0 0 4.5 8.25v10.5a1.75 1.75 0 0 0 1.75 1.75h8.5a1.75 1.75 0 0 0 1.75-1.75v-8.5z" fill={solidFill(s)} />
  {s ? null : <path d="M7.5 13.25h6M7.5 16.5h4" />}
</>);
export const CreditCard = glyph('invoices', s => <>
  <rect x="2.75" y="5.25" width="18.5" height="13.5" rx="2.5" fill={solidFill(s)} />
  <path d="M2.75 9.75h18.5" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
  {s ? null : <path d="M6.25 14.75h3.5M13.5 14.75h1" />}
</>);
/** Send History: the paper plane with a clock — past sends. */
export const SendHistory = glyph('send-history', s => <>
  <path d="M20.5 3.5 3.5 10.1l5.9 2.6M20.5 3.5l-3.2 8.4M20.5 3.5 9.4 12.7v4.05l2.2-2.3" fill={solidFill(s)} />
  <circle cx="16.75" cy="17" r="4" fill={s ? 'currentColor' : 'var(--c-surface, #fff)'} />
  <path d="M16.75 15.1V17l1.2.85" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
</>);
/** Trucks (fleet): a rig seen from the front. */
export const Fleet = glyph('fleet', s => <>
  <path d="M5.5 17.5V6.25A2.75 2.75 0 0 1 8.25 3.5h7.5a2.75 2.75 0 0 1 2.75 2.75V17.5" fill={solidFill(s)} />
  <path d="M7.75 11.25V7.5a1 1 0 0 1 1-1h6.5a1 1 0 0 1 1 1v3.75z" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
  <path d="M4.25 17.5h15.5M7 17.5v2.25M17 17.5v2.25M3.5 8.5v2.25M20.5 8.5v2.25" />
  {s ? null : <>{dot(8.6, 14.5, .95)}{dot(15.4, 14.5, .95)}</>}
</>);
export const Link2 = glyph('connections', s => <>
  <path d="M10 15.75H7.75a3.75 3.75 0 0 1 0-7.5H10" />
  <path d="M14 8.25h2.25a3.75 3.75 0 0 1 0 7.5H14" />
  <path d="M8.5 12h7" strokeWidth={s ? 2.5 : undefined} />
</>);
export const Settings = glyph('settings', s => <>
  <path d="M10.3 3.4a1.75 1.75 0 0 1 3.4 0l.2.75a1.75 1.75 0 0 0 2.5 1.05l.7-.38a1.75 1.75 0 0 1 2.4 2.4l-.38.7a1.75 1.75 0 0 0 1.05 2.5l.75.2a1.75 1.75 0 0 1 0 3.4l-.75.2a1.75 1.75 0 0 0-1.05 2.5l.38.7a1.75 1.75 0 0 1-2.4 2.4l-.7-.38a1.75 1.75 0 0 0-2.5 1.05l-.2.75a1.75 1.75 0 0 1-3.4 0l-.2-.75a1.75 1.75 0 0 0-2.5-1.05l-.7.38a1.75 1.75 0 0 1-2.4-2.4l.38-.7a1.75 1.75 0 0 0-1.05-2.5l-.75-.2a1.75 1.75 0 0 1 0-3.4l.75-.2A1.75 1.75 0 0 0 4.97 7.37l-.38-.7a1.75 1.75 0 0 1 2.4-2.4l.7.38a1.75 1.75 0 0 0 2.5-1.05z" fill={solidFill(s)} />
  <circle cx="12" cy="12" r="3" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
</>);

/** Automation → Workflows: a trigger node branching to two actions. */
export const Workflow = glyph('workflows', s => <>
  <rect x="3.25" y="9" width="6" height="6" rx="1.75" fill={solidFill(s)} />
  <rect x="15" y="3.75" width="5.75" height="5.25" rx="1.6" />
  <rect x="15" y="15" width="5.75" height="5.25" rx="1.6" />
  <path d="M9.25 12h2.5M11.75 6.4v11.2M11.75 6.4H15M11.75 17.6H15" />
</>);
/** Automation → Activity: a run trace with a pulse. */
export const Activity = glyph('activity', s => <>
  <rect x="3" y="4" width="18" height="16" rx="2.25" fill={solidFill(s)} />
  <path d="M6 12.5h2.6l1.6-3.5 2.8 7 1.7-3.5H18" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
</>);
/** Organization → Team & access: two people, the front one with a badge dot. */
export const Team = glyph('team', s => <>
  <circle cx="9" cy="8.25" r="3.25" fill={solidFill(s)} />
  <path d="M3.25 19.5v-.75a4.75 4.75 0 0 1 4.75-4.75h2a4.75 4.75 0 0 1 4.75 4.75v.75" fill={solidFill(s)} />
  <path d="M15.25 5.3a3.25 3.25 0 0 1 0 5.9M17.5 14.25a4.75 4.75 0 0 1 3.25 4.5v.75" />
</>);
/** Organization → Billing: a receipt with a torn foot. */
export const Receipt = glyph('billing', s => <>
  <path d="M6 3.25h12a.75.75 0 0 1 .75.75v16.25l-2.25-1.5-2.25 1.5-2.25-1.5-2.25 1.5-2.25-1.5-2.25 1.5V4A.75.75 0 0 1 6 3.25z" fill={solidFill(s)} />
  <path d="M8.75 8h6.5M8.75 11.5h6.5M8.75 15h3.5" stroke={s ? 'var(--c-sidebar, #fff)' : 'currentColor'} />
</>);

// ---------- Objects ----------
export const Car = glyph('car', s => <>
  <path d="M3 15.25V12.9a1.75 1.75 0 0 1 1.2-1.66l2.55-.85 2.2-3.08a1.75 1.75 0 0 1 1.43-.74h3.95a1.75 1.75 0 0 1 1.38.68l2.42 3.14 1.62.5A1.75 1.75 0 0 1 21 12.6v2.65a1 1 0 0 1-1 1h-.75" fill={solidFill(s)} />
  <path d="M14.75 16.25h-5.5M4.75 16.25H4a1 1 0 0 1-1-1" />
  <path d="M6.75 10.4h11.5M12.3 6.75v3.65" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
  <circle cx="7" cy="16.5" r="2" />
  <circle cx="17" cy="16.5" r="2" />
</>);
export const MapPin = glyph('pin', s => <>
  <path d="M12 21s6.75-5.75 6.75-11.25a6.75 6.75 0 0 0-13.5 0C5.25 15.25 12 21 12 21z" fill={solidFill(s)} />
  <circle cx="12" cy="9.75" r="2.5" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
</>);
export const Flag = glyph('flag', s => <>
  <path d="M5.5 21V3.75" />
  <path d="M5.5 4.25h12.25l-2.75 4.25 2.75 4.25H5.5" fill={solidFill(s)} />
</>);
export const Clock = glyph('clock', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M12 7v5.25l3.25 2" />
</>);
export const Calendar = glyph('calendar', () => <>
  <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
  <path d="M3.5 9.75h17M8 3v4M16 3v4" />
  <rect x="7" y="13" width="3.5" height="3.5" rx=".9" fill="currentColor" stroke="none" />
</>);
export const Camera = glyph('camera', () => <>
  <path d="M3 9.25A1.75 1.75 0 0 1 4.75 7.5h2.5l1.55-2.25h6.4l1.55 2.25h2.5A1.75 1.75 0 0 1 21 9.25v8.5a1.75 1.75 0 0 1-1.75 1.75H4.75A1.75 1.75 0 0 1 3 17.75z" />
  <circle cx="12" cy="13.25" r="3.5" />
  {dot(17.75, 10.25, .9)}
</>);
export const Image = glyph('image', () => <>
  <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
  <circle cx="8.5" cy="9.5" r="1.6" />
  <path d="m3.5 17.25 5-4.75 3.5 3.25 2.75-2.5 5.75 5.25" />
</>);
export const FileText = glyph('file', () => <>
  <path d="M14 3.5H7.25A1.75 1.75 0 0 0 5.5 5.25v13.5a1.75 1.75 0 0 0 1.75 1.75h9.5a1.75 1.75 0 0 0 1.75-1.75V8z" />
  <path d="M14 3.5V8h4.5M8.75 12.5h6.5M8.75 16h4.25" />
</>);
export const FilePlus2 = glyph('file-new', () => <>
  <path d="M14 3.5H7.25A1.75 1.75 0 0 0 5.5 5.25v13.5a1.75 1.75 0 0 0 1.75 1.75h9.5a1.75 1.75 0 0 0 1.75-1.75V8z" />
  <path d="M14 3.5V8h4.5M12 11.25v6M9 14.25h6" />
</>);
export const FileSpreadsheet = glyph('sheet', () => <>
  <path d="M14 3.5H7.25A1.75 1.75 0 0 0 5.5 5.25v13.5a1.75 1.75 0 0 0 1.75 1.75h9.5a1.75 1.75 0 0 0 1.75-1.75V8z" />
  <path d="M14 3.5V8h4.5" />
  <rect x="8.25" y="11.25" width="7.5" height="6.25" rx=".75" />
  <path d="M12 11.25v6.25M8.25 14.4h7.5" />
</>);
export const Folder = glyph('folder', () => <>
  <path d="M3.5 7.25A1.75 1.75 0 0 1 5.25 5.5h3.9l2.1 2.25h7.5a1.75 1.75 0 0 1 1.75 1.75v8.25a1.75 1.75 0 0 1-1.75 1.75H5.25a1.75 1.75 0 0 1-1.75-1.75z" />
  <path d="M3.5 10.25h17" />
</>);
export const Archive = glyph('archive', () => <>
  <rect x="3" y="4" width="18" height="4.25" rx="1.5" />
  <path d="M4.75 8.25v9.5a2 2 0 0 0 2 2h10.5a2 2 0 0 0 2-2v-9.5M10 12h4" />
</>);
export const ArchiveRestore = glyph('unarchive', () => <>
  <rect x="3" y="4" width="18" height="4.25" rx="1.5" />
  <path d="M4.75 8.25v9.5a2 2 0 0 0 2 2h10.5a2 2 0 0 0 2-2v-9.5M12 17.25v-6M9.5 13.5l2.5-2.5 2.5 2.5" />
</>);
export const Download = glyph('download', () => <>
  <path d="M12 3.75v11M7.75 10.75 12 15l4.25-4.25" />
  <path d="M4 15.75v2.5A1.75 1.75 0 0 0 5.75 20h12.5A1.75 1.75 0 0 0 20 18.25v-2.5" />
</>);
export const CloudUpload = glyph('upload', () => <>
  <path d="M7.25 18.5a4.5 4.5 0 0 1-.7-8.95 5.75 5.75 0 0 1 11.1 1.2 3.9 3.9 0 0 1-.4 7.75" />
  <path d="M12 20v-7.25M9.25 15.25 12 12.5l2.75 2.75" />
</>);
export const Copy = glyph('copy', () => <>
  <rect x="8.5" y="8.5" width="12" height="12" rx="2.25" />
  <path d="M15.5 8.5V5.75a2.25 2.25 0 0 0-2.25-2.25h-7.5A2.25 2.25 0 0 0 3.5 5.75v7.5a2.25 2.25 0 0 0 2.25 2.25H8.5" />
</>);
export const Lock = glyph('lock', () => <>
  <rect x="5" y="10.5" width="14" height="10" rx="2.25" />
  <path d="M8.25 10.5V7.75a3.75 3.75 0 0 1 7.5 0v2.75M12 14.5v2.5" />
</>);
export const Mail = glyph('mail', () => <>
  <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
  <path d="m3.75 7.25 8.25 6 8.25-6" />
</>);
export const Send = glyph('send', s => <>
  <path d="M20.5 3.5 3.5 10.1l6.25 2.75L20.5 3.5l-6.6 17-4.15-7.65" fill={solidFill(s)} />
</>);
export const MessageCircle = glyph('imessage', () => <>
  <path d="M12 4c4.7 0 8.5 3.15 8.5 7.1s-3.8 7.1-8.5 7.1c-.95 0-1.9-.13-2.75-.38L5 20l1.15-3.4C4.5 15.3 3.5 13.3 3.5 11.1 3.5 7.15 7.3 4 12 4z" />
</>);
export const MessageSquare = glyph('whatsapp', () => <>
  <path d="M4.25 20l1.3-3.85A8.25 8.25 0 1 1 8.6 19.1z" />
  <path d="M9.1 8.6c.2 2.9 2.8 5.65 6.15 6.25l.9-1.55-1.9-1-.8.8c-1-.45-1.9-1.35-2.35-2.35l.8-.8-1-1.9z" />
</>);
export const Triangle = glyph('drive', () => <>
  <path d="M8.75 4h6.5l6.25 10.75L18.25 20H5.75L2.5 14.75z" />
  <path d="M8.75 4l6.4 10.75H21.5M2.5 14.75 8.9 4.2M5.75 20l6.25-10.7" />
</>);
export const Globe = glyph('language', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M3.5 12h17M12 3.25c2.4 2.35 3.6 5.25 3.6 8.75s-1.2 6.4-3.6 8.75c-2.4-2.35-3.6-5.25-3.6-8.75S9.6 5.6 12 3.25z" />
</>);
export const Bell = glyph('bell', () => <>
  <path d="M6 16.25V11a6 6 0 0 1 12 0v5.25l1.5 2H4.5z" />
  <path d="M10 20.25a2.1 2.1 0 0 0 4 0M12 3v2" />
</>);
export const UserPlus = glyph('invite', () => <>
  <circle cx="9.5" cy="8" r="3.5" />
  <path d="M3.5 20a6 6 0 0 1 12 0M18.5 8.5v6M15.5 11.5h6" />
</>);
export const LogOut = glyph('sign-out', () => <>
  <path d="M9.5 20.5H6.25A2.25 2.25 0 0 1 4 18.25V5.75A2.25 2.25 0 0 1 6.25 3.5H9.5" />
  <path d="M15.5 16.5 20 12l-4.5-4.5M20 12H9.75" />
</>);
export const Sun = glyph('light', () => <>
  <circle cx="12" cy="12" r="4" />
  <path d="M12 2.75v1.75M12 19.5v1.75M2.75 12H4.5M19.5 12h1.75M5.45 5.45l1.25 1.25M17.3 17.3l1.25 1.25M5.45 18.55l1.25-1.25M17.3 6.7l1.25-1.25" />
</>);
export const Moon = glyph('dark', () => <>
  <path d="M19.75 14.75A8 8 0 0 1 9.25 4.25a8 8 0 1 0 10.5 10.5z" />
  {dot(17, 5.5, .8)}{dot(20, 8.75, .6)}
</>);
export const Contrast = glyph('system', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M12 3.25a8.75 8.75 0 0 1 0 17.5z" fill="currentColor" stroke="none" />
</>);
export const Grid2x2 = glyph('grid', () => <>
  <rect x="3.75" y="3.75" width="7" height="7" rx="1.75" />
  <rect x="13.25" y="3.75" width="7" height="7" rx="1.75" />
  <rect x="3.75" y="13.25" width="7" height="7" rx="1.75" />
  <rect x="13.25" y="13.25" width="7" height="7" rx="1.75" />
</>);
export const BarChart3 = glyph('summary', () => <>
  <path d="M3.5 20.5h17" />
  <rect x="5" y="11.5" width="3.25" height="6.5" rx="1" />
  <rect x="10.4" y="5.5" width="3.25" height="12.5" rx="1" />
  <rect x="15.75" y="13.75" width="3.25" height="4.25" rx="1" />
</>);
export const Columns3 = glyph('columns', () => <>
  <rect x="3.5" y="4.5" width="17" height="15" rx="2.25" />
  <path d="M9.25 4.5v15M14.75 4.5v15" />
</>);
export const SlidersHorizontal = glyph('filters', () => <>
  <path d="M4 7h8.5M17.5 7H20M4 17h2.5M11.5 17H20" />
  <circle cx="15" cy="7" r="2.25" />
  <circle cx="9" cy="17" r="2.25" />
</>);
export const List = glyph('menu', () => <>
  <path d="M9 6.5h11M9 12h11M9 17.5h11" />
  {dot(5, 6.5, 1.2)}{dot(5, 12, 1.2)}{dot(5, 17.5, 1.2)}
</>);
export const Search = glyph('search', () => <>
  <circle cx="10.5" cy="10.5" r="6.5" />
  <path d="m15.25 15.25 5 5" />
</>);
export const RotateCcw = glyph('reset', () => <>
  <path d="M4.25 12a7.75 7.75 0 1 0 2.3-5.5L4.25 8.75" />
  <path d="M4.25 4v4.75H9" />
</>);
/** Switch workspace: two arrows trading places. */
export const Repeat = glyph('switch', () => <>
  <path d="M4.5 9.5h13.25M14.5 5.75l3.75 3.75-3.75 3.75" />
  <path d="M19.5 14.5H6.25M9.5 10.75 5.75 14.5l3.75 3.75" />
</>);
export const ExternalLink = glyph('open', () => <>
  <path d="M13.5 4.5h6v6M19.5 4.5 11 13" />
  <path d="M17.5 13.75v4.5a1.75 1.75 0 0 1-1.75 1.75H5.75A1.75 1.75 0 0 1 4 18.25V8.25A1.75 1.75 0 0 1 5.75 6.5h4.5" />
</>);

/** AI assistance marker: one four-point spark and a small one. */
export const Spark = glyph('spark', s => <>
  <path d="M10.5 3.5c.5 3.6 2.4 5.5 6 6-3.6.5-5.5 2.4-6 6-.5-3.6-2.4-5.5-6-6 3.6-.5 5.5-2.4 6-6z" fill={solidFill(s)} />
  <path d="M18 14.5c.25 1.6 1 2.35 2.5 2.5-1.5.25-2.25 1-2.5 2.5-.25-1.5-1-2.25-2.5-2.5 1.5-.15 2.25-.9 2.5-2.5z" />
</>);
/** Company playbook: an open binder with a check. */
export const Playbook = glyph('playbook', s => <>
  <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H18a1 1 0 0 1 1 1v14H6.75A1.75 1.75 0 0 0 5 19.75z" fill={solidFill(s)} />
  <path d="M5 19.75A1.75 1.75 0 0 0 6.75 21.5H19V18" />
  <path d="m9 10.25 2 2 4-4.25" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
</>);
/** Policy / mandatory rule: a shield with a bar. */
export const Shield = glyph('shield', s => <>
  <path d="M12 3.25 19 6v5.6c0 4.3-2.9 7.6-7 9.15-4.1-1.55-7-4.85-7-9.15V6z" fill={solidFill(s)} />
  <path d="M9 12h6" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
</>);
/** Credential: a key (never revealed, only replaced). */
export const Key = glyph('key', s => <>
  <circle cx="8" cy="15.5" r="4.25" fill={solidFill(s)} />
  <path d="m11 12.5 8.25-8.25M16.25 7.25l2.5 2.5M14 9.5l1.75 1.75" />
</>);
/** Usage window: a gauge with a needle. */
export const Gauge = glyph('gauge', s => <>
  <path d="M3.75 16.5a8.25 8.25 0 1 1 16.5 0" fill={solidFill(s)} />
  <path d="M3 19.5h18" />
  <path d="m12 16.25 3.5-4.5" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
  {dot(12, 16.25, 1.2)}
</>);
export const Pause = glyph('pause', s => <>
  <rect x="6.25" y="4.75" width="4" height="14.5" rx="1.25" fill={solidFill(s)} />
  <rect x="13.75" y="4.75" width="4" height="14.5" rx="1.25" fill={solidFill(s)} />
</>);
export const Play = glyph('play', s => <path d="M7.5 4.9a1 1 0 0 1 1.5-.87l10.2 6.1a1 1 0 0 1 0 1.72L9 17.97a1 1 0 0 1-1.5-.87z" fill={solidFill(s)} />);
/** Branching conflict: two paths meeting at one point. */
export const Merge = glyph('conflict', s => <>
  <circle cx="6" cy="5.5" r="2.25" fill={solidFill(s)} />
  <circle cx="18" cy="5.5" r="2.25" fill={solidFill(s)} />
  <circle cx="12" cy="18.5" r="2.25" fill={solidFill(s)} />
  <path d="M6 7.75v1.5a4 4 0 0 0 2 3.46l4 2.29M18 7.75v1.5a4 4 0 0 1-2 3.46l-4 2.29" />
</>);
/** Snooze: a clock with a small z. */
export const Snooze = glyph('snooze', s => <>
  <circle cx="11" cy="13" r="7.25" fill={solidFill(s)} />
  <path d="M11 9.25V13l2.25 1.5" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
  <path d="M16.5 3.25h3.75l-3.75 4h3.75" />
</>);
/** Photo gallery: stacked frames. */
export const Gallery = glyph('gallery', s => <>
  <rect x="6.5" y="6.5" width="14" height="12" rx="2" fill={solidFill(s)} />
  <path d="M3.5 15.5V5.25A1.75 1.75 0 0 1 5.25 3.5H16" />
  <path d="m8.75 16.25 3.5-3.75 2.5 2.25 1.75-1.5 2.25 3" stroke={s ? 'var(--c-surface, #fff)' : 'currentColor'} />
</>);
/** Shared with a partner: a person inside a doorway. */
export const Portal = glyph('portal', s => <>
  <path d="M4 20.5V5a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 16 5v15.5" fill={solidFill(s)} />
  <path d="M2.5 20.5h19M16 12.25h4.25m-1.75-2 2 2-2 2" />
  {dot(12.5, 12.25, 1)}
</>);

// ---------- Status ----------
export const Check = glyph('check', () => <path d="m5 12.5 4.5 4.5L19 7.5" />);
export const CircleCheck = glyph('ok', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="m8.1 12.25 2.65 2.65 5.15-5.4" />
</>);
export const CircleAlert = glyph('error', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M12 7.5v5.25" />{dot(12, 16.25)}
</>);
export const CircleHelp = glyph('help', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M9.6 9.5a2.5 2.5 0 1 1 3.55 2.25c-.75.35-1.15.95-1.15 1.75v.25" />{dot(12, 16.75)}
</>);
export const Info = glyph('info', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M12 11v5.25" />{dot(12, 7.75)}
</>);
export const AlertTriangle = glyph('warning', () => <>
  <path d="M10.45 4.4a1.8 1.8 0 0 1 3.1 0l7.2 12.6a1.8 1.8 0 0 1-1.55 2.7H4.8a1.8 1.8 0 0 1-1.55-2.7z" />
  <path d="M12 9.5v4" />{dot(12, 16.4)}
</>);
export const CirclePlus = glyph('add', () => <>
  <circle cx="12" cy="12" r="8.75" />
  <path d="M12 8v8M8 12h8" />
</>);

// ---------- Direction ----------
export const X = glyph('close', () => <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />);
export const ChevronDown = glyph('chevron-down', () => <path d="m6.5 9.5 5.5 5.5 5.5-5.5" />);
export const ChevronUp = glyph('chevron-up', () => <path d="m6.5 14.5 5.5-5.5 5.5 5.5" />);
export const ChevronLeft = glyph('chevron-left', () => <path d="M14.5 6.5 9 12l5.5 5.5" />);
export const ChevronRight = glyph('chevron-right', () => <path d="M9.5 6.5 15 12l-5.5 5.5" />);
export const ArrowDown = glyph('arrow-down', () => <path d="M12 4.75v14.5M6.5 13.75 12 19.25l5.5-5.5" />);
export const ArrowUp = glyph('arrow-up', () => <path d="M12 19.25V4.75M6.5 10.25 12 4.75l5.5 5.5" />);
export const ArrowRight = glyph('arrow-right', () => <path d="M4.75 12h14.5M13.75 6.5l5.5 5.5-5.5 5.5" />);
export const ArrowUpDown = glyph('sort', () => <path d="M8 19.5V4.5M4.75 7.75 8 4.5l3.25 3.25M16 4.5v15M12.75 16.25 16 19.5l3.25-3.25" />);

/** Every glyph, for the icon sheet and uniqueness checks. */
export const ALL_GLYPHS = {
  House, Building2, Truck, MapPinned, DocumentStack, CreditCard, SendHistory, Fleet, Link2, Settings, Workflow, Activity, Team, Receipt,
  Spark, Playbook, Shield, Key, Gauge, Pause, Play, Merge, Snooze, Gallery, Portal,
  Car, MapPin, Flag, Clock, Calendar, Camera, Image, FileText, FilePlus2, FileSpreadsheet, Folder, Archive, ArchiveRestore, CloudUpload,
  Copy, Download, Lock, Mail, Send, MessageCircle, MessageSquare, Triangle, Globe, Bell, UserPlus, LogOut, Sun, Moon, Contrast, Grid2x2,
  BarChart3, Columns3, SlidersHorizontal, List, Search, RotateCcw, Repeat, ExternalLink,
  Check, CircleCheck, CircleAlert, CircleHelp, Info, AlertTriangle, CirclePlus,
  X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowDown, ArrowUp, ArrowRight, ArrowUpDown,
};
